import { useEffect, useSyncExternalStore } from 'react';
import { normalizeId } from '@studio/models/identifier';
import type { RoomRecord } from '@studio/models/room';
import { AgentRegistryMembershipRole as Role } from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import {
  BillingRegistryChangeRoomPlanRequest,
  BillingRegistryGetRoomBillingInfoRequest,
  BillingRegistryGetUsageRequest
} from '@tryiris-ai/common.protojs/api/v1/billing_registry';
import {
  BillingRegistryChangePlan,
  BillingRegistryGetBillingInfo,
  BillingRegistryGetUsage
} from '@tryiris-ai/mailroom-helper/billing';
import { authStore } from '#~/auth/store.js';
import { isElectronApp } from '#~/electron/utils.js';
import { useAuth0 } from '#~/auth/use-auth0.js';
import { config } from '#~/config/index.js';
import { depot } from '#~/depot/index.js';
import { nats } from '#~/nats/index.js';
import { toIdFromHex } from '#~/api/utils.js';
import { forceRefreshBilling } from './limits.js';
import { attachPlanPurchaseReturns } from './payment-return.js';
import { PlanPurchaseCoordinator } from './plan-purchase.js';
import type { PlanPurchaseView } from './plan-purchase.js';

interface PurchaseContext {
  store: PlanPurchaseCoordinator;
  isCurrent: () => boolean;
  consumers: number;
  retirement: number;
  closed: boolean;
  detach?: () => void;
  stopListening?: () => void;
}
const stores = new Map<string, PurchaseContext>();
const activeActors = new Map<string, string | undefined>();
// Native auth changes before the asynchronously mapped Iris user record.
const currentActor = (vendor: string): string | undefined => {
  if (!isElectronApp()) return activeActors.get(vendor);
  const subject: unknown = authStore.getState().userInfo?.sub;
  return typeof subject === 'string' && subject !== '' ? subject : undefined;
};
const empty: Readonly<Record<string, PlanPurchaseView>> = {};
const subscribeEmpty = () => () => {};
const emptySnapshot = () => empty;
const noCurrentAccount = () => false;

function purchaseStore(
  actorKey: string | undefined,
  getToken: () => Promise<string>
): PurchaseContext | undefined {
  const vendor = config.vendor;
  if ((vendor !== 'iris' && vendor !== 'ptzoptics') || actorKey === undefined) return undefined;
  const key = `${vendor}:${actorKey}`;
  const existing = stores.get(key);
  if (existing !== undefined) return existing;
  const context: PurchaseContext = {
    store: undefined!,
    isCurrent: () => !context.closed && currentActor(vendor) === actorKey,
    consumers: 0,
    retirement: 0,
    closed: false
  };
  const assertCurrent = () => {
    if (!context.isCurrent()) throw new Error('purchase account changed');
  };
  const token = async () => {
    assertCurrent();
    const credentials = await getToken();
    assertCurrent();
    return credentials;
  };
  const fresh = { force: true, cacheTTL: 0, staleOnError: false };
  const readBilling = (roomId: string, credentials: string) =>
    BillingRegistryGetBillingInfo.request(
      nats.cloudConnection(),
      BillingRegistryGetRoomBillingInfoRequest.fromPartial({
        roomID: toIdFromHex(roomId)!,
        token: credentials
      }),
      fresh
    );
  const reconcile = async (roomId: string, sessionId: string, cancelPurchase = false) => {
    const credentials = await token();
    const id = normalizeId<RoomRecord['id']>(roomId);
    if (id === undefined) throw new Error('purchase Studio identity is unresolved');
    // Members can observe purchases; only a current owner may reconcile billing mutations.
    if (
      depot.getState().roomRecords[id]?.permissions?.role !==
      Role.AGENT_REGISTRY_MEMBERSHIP_ROLE_OWNER
    ) {
      const response = await readBilling(roomId, credentials);
      assertCurrent();
      if (response.error !== undefined || response.purchase?.checkoutSessionId !== sessionId)
        throw new Error('purchase confirmation unavailable');
      return response.purchase;
    }
    const response = await BillingRegistryChangePlan.request(
      nats.cloudConnection(),
      BillingRegistryChangeRoomPlanRequest.fromPartial({
        roomID: toIdFromHex(roomId)!,
        token: credentials,
        checkoutSessionId: sessionId,
        cancelPurchase
      }),
      // Confirmation is a status read carried by the mutation API. Its
      // default request cache must not retain an earlier open payment.
      fresh
    );
    assertCurrent();
    if (response.error !== undefined || response.purchase === undefined)
      throw new Error('purchase confirmation unavailable');
    return response.purchase;
  };
  context.store = new PlanPurchaseCoordinator({
    vendor,
    actorKey,
    isCurrent: context.isCurrent,
    // Access can throw in privacy-restricted browsers. The adapter is lazy so
    // the coordinator can retain its in-memory receipt on storage failure.
    storage: {
      getItem: (key) => window.localStorage.getItem(key),
      setItem: (key, value) => window.localStorage.setItem(key, value)
    },
    confirm: (roomId, sessionId) => reconcile(roomId, sessionId),
    cancel: (roomId, sessionId) => reconcile(roomId, sessionId, true),
    canCancel: (roomId) =>
      context.isCurrent() &&
      depot.getState().roomRecords[normalizeId<RoomRecord['id']>(roomId)!]?.permissions?.role ===
        Role.AGENT_REGISTRY_MEMBERSHIP_ROLE_OWNER,
    load: async (roomId) => {
      const credentials = await token();
      // A cached pre-payment trial/Free response is not the paid Studio's
      // authoritative refresh. Failed reads retain the existing attempt.
      const [billing, usage] = await Promise.all([
        readBilling(roomId, credentials),
        BillingRegistryGetUsage.request(
          nats.cloudConnection(),
          BillingRegistryGetUsageRequest.fromPartial({
            roomID: toIdFromHex(roomId)!,
            token: credentials
          }),
          fresh
        )
      ]);
      assertCurrent();
      return { billing, usage };
    },
    refresh: async (roomId) => {
      assertCurrent();
      await forceRefreshBilling(roomId, context.isCurrent);
      assertCurrent();
    }
  });
  stores.set(key, context);
  return context;
}

// Mounted with the actual Studio, independently of the billing panel. Focus,
// visibility and reopening resume the same durable attempt, never repurchase.
export function usePlanPurchases(): {
  actorKey: string | undefined;
  isCurrentAccount: () => boolean;
  store: PlanPurchaseCoordinator | undefined;
  purchases: Readonly<Record<string, PlanPurchaseView>>;
} {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const subject: unknown = user?.sub;
  const actorKey =
    isAuthenticated && typeof subject === 'string' && subject !== '' ? subject : undefined;
  const vendor = config.vendor;
  activeActors.set(vendor, actorKey);
  const context = purchaseStore(actorKey, getAccessTokenSilently);
  const store = context?.store;
  const purchases = useSyncExternalStore(
    store?.subscribe ?? subscribeEmpty,
    store?.snapshot ?? emptySnapshot
  );
  useEffect(() => {
    if (context === undefined || (vendor !== 'iris' && vendor !== 'ptzoptics')) return;
    context.retirement++;
    if (context.consumers++ === 0 && context.detach === undefined) {
      context.detach = attachPlanPurchaseReturns(vendor, context.store, () => currentActor(vendor));
      const resume = () => context.store.resumePending();
      const visible = () => {
        if (document.visibilityState === 'visible') resume();
      };
      window.addEventListener('focus', resume);
      document.addEventListener('visibilitychange', visible);
      context.stopListening = () => {
        window.removeEventListener('focus', resume);
        document.removeEventListener('visibilitychange', visible);
      };
    }
    context.store.resumePending();
    return () => {
      context.consumers--;
      const retirement = ++context.retirement;
      // StrictMode reacquires synchronously; closing only Billing leaves the Studio consumer alive.
      queueMicrotask(() => {
        if (context.consumers !== 0 || context.retirement !== retirement) return;
        context.closed = true;
        context.detach?.();
        context.stopListening?.();
        context.store.dispose();
        const key = `${vendor}:${context.store.actorKey}`;
        if (stores.get(key) === context) stores.delete(key);
      });
    };
  }, [context, vendor]);
  return {
    actorKey,
    isCurrentAccount: context?.isCurrent ?? noCurrentAccount,
    store,
    purchases
  };
}
